/**
 * Walking a paged list endpoint to the end, for a register exported as a file.
 *
 * Shared by every export that writes its file in the browser from rows the
 * list endpoint already authorised (see `downloadCsv` in `lib/download.ts`).
 */

/** The largest page the server will serve, so the fewest requests are made. */
export const EXPORT_PAGE_SIZE = 100;

/**
 * How many pages an export will walk before it stops.
 *
 * A guard rather than a preference: without it a mis-set filter on a large
 * institute becomes an unbounded loop of requests. What was left out is always
 * reported back to the caller rather than quietly trimmed.
 */
const MAX_PAGES = 40;

export interface ExportResult {
  rows: number;
  /** How many rows the cap left out. Zero unless the register is very large. */
  skipped: number;
}

/** Walks a paged endpoint to the end, or to the cap, whichever comes first. */
export async function collectPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; meta: { total: number; totalPages: number } }>,
): Promise<{ items: T[]; skipped: number }> {
  const items: T[] = [];
  let total = 0;
  let totalPages = 1;

  for (let page = 1; page <= Math.min(totalPages, MAX_PAGES); page += 1) {
    const result = await fetchPage(page);
    items.push(...result.items);
    total = result.meta.total;
    totalPages = result.meta.totalPages;
  }

  return { items, skipped: Math.max(0, total - items.length) };
}
